import { useState, useEffect, useRef } from 'react';
import { Icon } from '@atoms/index';
import { useLocationStore } from '@store/locationStore';
import { geoService, type City } from '@services/geoService';

interface CitySelectorProps {
  onCitySelect?: () => void;
  variant?: 'header' | 'inline';
}

const CitySelector = ({ onCitySelect, variant = 'header' }: CitySelectorProps) => {
  const { serviceZone, selectedCity, setSelectedCity } = useLocationStore();
  const [isOpen, setIsOpen] = useState(false);
  const [cities, setCities] = useState<City[]>([]);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const displayCityName = selectedCity?.name ?? serviceZone?.city ?? null;
  const displayLabel = displayCityName ?? 'Şehir seçin';
  const displayLabelShort = displayLabel.length > 20 ? `${displayLabel.slice(0, 17)}…` : displayLabel;

  useEffect(() => {
    if (isOpen && cities.length === 0) {
      setIsLoadingCities(true);
      geoService
        .getCities()
        .then((res) => setCities(res.cities ?? []))
        .catch(() => setCities([]))
        .finally(() => setIsLoadingCities(false));
    }
  }, [isOpen, cities.length]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const filteredCities = searchQuery.trim()
    ? cities.filter(
        (c) =>
          c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.slug.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : cities;

  const handleSelect = (city: City) => {
    setSelectedCity({ id: city.id, name: city.name, slug: city.slug });
    setIsOpen(false);
    setSearchQuery('');
    onCitySelect?.();
  };

  const isCompact = variant === 'header';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 bg-white border border-gray-200 rounded-2xl hover:border-primary/40 hover:bg-gray-50/80 transition-all text-sm font-medium text-gray-700 ${
          isCompact
            ? 'px-3 py-2.5 max-w-[180px]'
            : 'px-4 py-3 shadow-sm hover:shadow-md'
        }`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Şehir seç"
      >
        <Icon name="location" size={18} className="text-primary flex-shrink-0" />
        <span className="truncate">{displayLabelShort}</span>
        <Icon
          name="chevron-down"
          size={16}
          className={`text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div
          className="absolute top-full left-0 mt-2 w-72 bg-white rounded-2xl shadow-xl border border-gray-200 z-50 max-h-[320px] flex flex-col overflow-hidden"
          role="listbox"
        >
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <Icon
                name="search"
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Şehir ara..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 text-sm"
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1 min-h-0">
            {isLoadingCities ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredCities.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-500">
                {searchQuery ? 'Şehir bulunamadı' : 'Şehir listesi yükleniyor...'}
              </div>
            ) : (
              <ul className="py-2">
                {filteredCities.map((city) => (
                  <li key={city.id}>
                    <button
                      type="button"
                      onClick={() => handleSelect(city)}
                      className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors ${
                        selectedCity?.id === city.id
                          ? 'bg-primary/10 text-primary'
                          : 'text-gray-800 hover:bg-gray-50'
                      }`}
                      role="option"
                      aria-selected={selectedCity?.id === city.id}
                    >
                      {city.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CitySelector;
